/**
 * Single Angular service for the syndic feature module.
 *
 *   - Observables for buildings, units, expenses, charge statements,
 *     announcements, documents under the active syndic workspace.
 *   - Typed wrappers for the four Cloud Function callables:
 *       createSyndicWorkspace
 *       sendSyndicBuildingInvitation
 *       acceptSyndicBuildingInvitation
 *       generateChargeStatement
 *
 * No direct component talks to Firestore — everything goes through here.
 * This keeps Firestore-specific bits (collection paths, query shape) in
 * exactly one place.
 */

import { Injectable, inject } from '@angular/core';
import { BuildingUnitDataSource } from '@shared/definitions/building-unit-data-source.token';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  query,
  orderBy,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable } from 'rxjs';
import { WorkspaceStore } from '../../../shared/services/workspace.store';
import { deepStripUndefined } from '../../../shared/utils/firestore-clean';
import {
  Building,
  BuildingAnnouncement,
  BuildingCommunication,
  BuildingCommunicationType,
  BuildingDocument,
  BuildingExpense,
  ChargeAdjustmentDraft,
  ChargeStatement,
  ChargeStatementReceipt,
  CommunicationChannel,
  ExpenseAllocation,
  ExpenseCategory,
  Unit,
  UnitType,
} from '../../../shared/definitions/syndic';

@Injectable({ providedIn: 'root' })
export class SyndicService implements BuildingUnitDataSource {
  private readonly db = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly workspaceStore = inject(WorkspaceStore);

  // -------------------------------------------------------------------------
  // Path helpers
  // -------------------------------------------------------------------------

  private get syndicWorkspaceId(): string {
    const ws = this.workspaceStore.currentWorkspace();
    if (!ws) {
      throw new Error('SyndicService: no current workspace');
    }
    if (ws.type !== 'syndic') {
      throw new Error(`SyndicService: current workspace is type=${ws.type}, expected 'syndic'`);
    }
    const wsId = ws.workspaceId;
    if (!wsId) {
      throw new Error('SyndicService: workspace has no workspaceId');
    }
    return wsId;
  }

  private buildingsPath(): string {
    return `workspaces/${this.syndicWorkspaceId}/buildings`;
  }
  private buildingPath(buildingId: string): string {
    return `${this.buildingsPath()}/${buildingId}`;
  }
  private unitsPath(buildingId: string): string {
    return `${this.buildingPath(buildingId)}/units`;
  }
  private expensesPath(buildingId: string): string {
    return `${this.buildingPath(buildingId)}/expenses`;
  }
  private chargeStatementsPath(buildingId: string): string {
    return `${this.buildingPath(buildingId)}/chargeStatements`;
  }
  private announcementsPath(buildingId: string): string {
    return `${this.buildingPath(buildingId)}/announcements`;
  }
  private documentsPath(buildingId: string): string {
    return `${this.buildingPath(buildingId)}/documents`;
  }
  private communicationsPath(buildingId: string): string {
    return `${this.buildingPath(buildingId)}/communications`;
  }

  // -------------------------------------------------------------------------
  // Observables — buildings
  // -------------------------------------------------------------------------

  buildings$(): Observable<Building[]> {
    const ref = collection(this.db, this.buildingsPath());
    return collectionData(query(ref, orderBy('createdAt', 'desc')), {
      idField: 'buildingId',
    }) as Observable<Building[]>;
  }

  building$(buildingId: string): Observable<Building | undefined> {
    return docData(doc(this.db, this.buildingPath(buildingId)), {
      idField: 'buildingId',
    }) as Observable<Building | undefined>;
  }

  async createBuilding(input: {
    buildingId?: string;
    name: string;
    address: Building['address'];
    unitCount: number;
    syndicContact: Building['syndicContact'];
    contacts?: Building['contacts'];
    baseDeedReference?: string;
    insurancePolicyReference?: string;
  }): Promise<string> {
    const ref = doc(collection(this.db, this.buildingsPath()));
    const buildingId = input.buildingId ?? ref.id;
    await setDoc(doc(this.db, `${this.buildingsPath()}/${buildingId}`), {
      ...deepStripUndefined({
        buildingId,
        name: input.name,
        address: input.address,
        unitCount: input.unitCount,
        syndicContact: input.syndicContact,
        contacts: input.contacts ?? [],
        baseDeedReference: input.baseDeedReference ?? null,
        insurancePolicyReference: input.insurancePolicyReference ?? null,
        status: 'active',
      }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return buildingId;
  }

  async updateBuilding(buildingId: string, patch: Partial<Building>): Promise<void> {
    await updateDoc(doc(this.db, this.buildingPath(buildingId)), {
      ...deepStripUndefined(patch),
      updatedAt: serverTimestamp(),
    });
  }

  async archiveBuilding(buildingId: string): Promise<void> {
    await this.updateBuilding(buildingId, { status: 'archived' });
  }

  // -------------------------------------------------------------------------
  // Observables — units
  // -------------------------------------------------------------------------

  units$(buildingId: string): Observable<Unit[]> {
    const ref = collection(this.db, this.unitsPath(buildingId));
    return collectionData(query(ref, orderBy('unitNumber', 'asc')), {
      idField: 'unitId',
    }) as Observable<Unit[]>;
  }

  async createUnit(input: {
    buildingId: string;
    unitNumber: string;
    quotites: number;
    type: UnitType;
    floor?: number;
    surfaceM2?: number;
  }): Promise<string> {
    const ref = doc(collection(this.db, this.unitsPath(input.buildingId)));
    const unitId = ref.id;
    await setDoc(ref, {
      ...deepStripUndefined({
        unitId,
        buildingId: input.buildingId,
        unitNumber: input.unitNumber,
        quotites: input.quotites,
        type: input.type,
        floor: input.floor ?? null,
        surfaceM2: input.surfaceM2 ?? null,
      }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return unitId;
  }

  async updateUnit(buildingId: string, unitId: string, patch: Partial<Unit>): Promise<void> {
    await updateDoc(doc(this.db, `${this.unitsPath(buildingId)}/${unitId}`), {
      ...deepStripUndefined(patch),
      updatedAt: serverTimestamp(),
    });
  }

  // -------------------------------------------------------------------------
  // Observables — expenses
  // -------------------------------------------------------------------------

  expenses$(buildingId: string): Observable<BuildingExpense[]> {
    const ref = collection(this.db, this.expensesPath(buildingId));
    return collectionData(query(ref, orderBy('date', 'desc')), {
      idField: 'expenseId',
    }) as Observable<BuildingExpense[]>;
  }

  async createExpense(input: {
    buildingId: string;
    date: Date;
    amount: number;
    currency: string;
    category: ExpenseCategory;
    description: string;
    supplier?: string;
    providerUID?: string;
    allocation: ExpenseAllocation;
  }): Promise<string> {
    const ref = doc(collection(this.db, this.expensesPath(input.buildingId)));
    const expenseId = ref.id;
    await setDoc(ref, {
      expenseId,
      buildingId: input.buildingId,
      date: Timestamp.fromDate(input.date),
      amount: input.amount,
      currency: input.currency,
      category: input.category,
      description: input.description,
      supplier: input.supplier ?? null,
      providerUID: input.providerUID ?? null,
      allocation: input.allocation,
      paymentStatus: 'unpaid',
      paidAmountCents: 0,
      reconciled: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return expenseId;
  }

  // -------------------------------------------------------------------------
  // Observables — charge statements
  // -------------------------------------------------------------------------

  chargeStatements$(buildingId: string): Observable<ChargeStatement[]> {
    const ref = collection(this.db, this.chargeStatementsPath(buildingId));
    return collectionData(query(ref, orderBy('periodStart', 'desc')), {
      idField: 'statementId',
    }) as Observable<ChargeStatement[]>;
  }

  /** Delivery receipts (accusé de réception) for one charge statement. */
  chargeStatementReceipts$(buildingId: string, statementId: string): Observable<ChargeStatementReceipt[]> {
    const ref = collection(
      this.db,
      `${this.chargeStatementsPath(buildingId)}/${statementId}/chargeStatementReceipts`,
    );
    return collectionData(ref, { idField: 'unitId' }) as Observable<ChargeStatementReceipt[]>;
  }

  // -------------------------------------------------------------------------
  // Observables — announcements
  // -------------------------------------------------------------------------

  announcements$(buildingId: string): Observable<BuildingAnnouncement[]> {
    const ref = collection(this.db, this.announcementsPath(buildingId));
    return collectionData(query(ref, orderBy('publishedAt', 'desc')), {
      idField: 'announcementId',
    }) as Observable<BuildingAnnouncement[]>;
  }

  async createAnnouncement(input: {
    buildingId: string;
    type: BuildingAnnouncement['type'];
    title: string;
    body: string;
    scopeUnitIds?: string[];
  }): Promise<string> {
    const ref = doc(collection(this.db, this.announcementsPath(input.buildingId)));
    const announcementId = ref.id;
    await setDoc(ref, {
      announcementId,
      buildingId: input.buildingId,
      type: input.type,
      title: input.title,
      body: input.body,
      scopeUnitIds: input.scopeUnitIds ?? [],
      publishedAt: serverTimestamp(),
      createdBy: this.workspaceStore.currentWorkspace()?.ownerId ?? '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return announcementId;
  }

  // -------------------------------------------------------------------------
  // Observables — documents
  // -------------------------------------------------------------------------

  documents$(buildingId: string): Observable<BuildingDocument[]> {
    const ref = collection(this.db, this.documentsPath(buildingId));
    return collectionData(query(ref, orderBy('uploadedAt', 'desc')), {
      idField: 'documentId',
    }) as Observable<BuildingDocument[]>;
  }

  async addDocumentReference(input: {
    buildingId: string;
    category: BuildingDocument['category'];
    name: string;
    url: string;
    sizeBytes?: number;
  }): Promise<string> {
    const ref = doc(collection(this.db, this.documentsPath(input.buildingId)));
    const documentId = ref.id;
    await setDoc(ref, {
      documentId,
      buildingId: input.buildingId,
      category: input.category,
      name: input.name,
      url: input.url,
      sizeBytes: input.sizeBytes ?? null,
      uploadedBy: this.workspaceStore.currentWorkspace()?.ownerId ?? '',
      uploadedAt: serverTimestamp(),
    });
    return documentId;
  }

  // -------------------------------------------------------------------------
  // Observables — communications
  // -------------------------------------------------------------------------

  communications$(buildingId: string): Observable<BuildingCommunication[]> {
    const ref = collection(this.db, this.communicationsPath(buildingId));
    return collectionData(query(ref, orderBy('createdAt', 'desc')), {
      idField: 'communicationId',
    }) as Observable<BuildingCommunication[]>;
  }

  // -------------------------------------------------------------------------
  // Callable wrappers
  // -------------------------------------------------------------------------

  async createSyndicWorkspace(input: { workspaceName?: string; firmName?: string } = {}) {
    const fn = httpsCallable<typeof input, { success: boolean; workspaceId: string; workspaceName: string }>(
      this.functions,
      'createSyndicWorkspace',
    );
    const res = await fn(input);
    return res.data;
  }

  async sendBuildingInvitation(input: {
    buildingId: string;
    unitId: string;
    ownerEmail: string;
    personalMessage?: string;
  }) {
    const fn = httpsCallable<
      typeof input & { syndicWorkspaceId: string },
      { success: boolean; invitationId: string; token: string }
    >(this.functions, 'sendSyndicBuildingInvitation');
    const res = await fn({ ...input, syndicWorkspaceId: this.syndicWorkspaceId });
    return res.data;
  }

  /**
   * Look up an invitation's public context (building/unit/syndic) for the
   * acceptance landing page. Public callable — no auth required.
   */
  async getInvitationByToken(token: string) {
    const fn = httpsCallable<
      { token: string },
      {
        found: boolean;
        status?: string;
        syndicName?: string | null;
        buildingName?: string | null;
        buildingAddress?: { street?: string; city?: string; postalCode?: string; country?: string } | null;
        unitNumber?: string | null;
        ownerEmail?: string | null;
        expiresAt?: string | null;
      }
    >(this.functions, 'getSyndicInvitationByToken');
    const res = await fn({ token });
    return res.data;
  }

  /**
   * Accept a syndic-building invitation. `ownerWorkspaceId` / `ownerPropertyId`
   * are OPTIONAL: when omitted the backend reuses the caller's owner-like
   * workspace (or creates a free one) and auto-creates a pre-filled property
   * (zero-friction onboarding). Provide them to link an EXISTING property.
   */
  async acceptBuildingInvitation(input: {
    invitationToken: string;
    ownerWorkspaceId?: string;
    ownerPropertyId?: string;
  }) {
    const fn = httpsCallable<
      typeof input,
      {
        success: boolean;
        linkId?: string;
        buildingId: string;
        unitId: string;
        ownerWorkspaceId?: string;
        ownerPropertyId?: string;
        createdWorkspace?: boolean;
        createdProperty?: boolean;
        alreadyAccepted?: boolean;
        alreadyConnected?: boolean;
      }
    >(this.functions, 'acceptSyndicBuildingInvitation');
    const res = await fn(input);
    return res.data;
  }

  async finaliseChargeStatement(input: { buildingId: string; statementId: string }) {
    const fn = httpsCallable<
      { syndicWorkspaceId: string; buildingId: string; statementId: string },
      {
        success: boolean;
        statementId: string;
        reconciledExpenseCount?: number;
        connectedOwnerCount?: number;
        alreadyFinalised?: boolean;
      }
    >(this.functions, 'finaliseChargeStatement');
    const res = await fn({
      syndicWorkspaceId: this.syndicWorkspaceId,
      buildingId: input.buildingId,
      statementId: input.statementId,
    });
    return res.data;
  }

  async generateChargeStatement(input: {
    buildingId: string;
    periodStart: Date;
    periodEnd: Date;
    currency: string;
    expenseIds?: string[];
    notes?: string;
  }) {
    const fn = httpsCallable<
      {
        syndicWorkspaceId: string;
        buildingId: string;
        periodStart: string;
        periodEnd: string;
        currency: string;
        expenseIds?: string[];
        notes?: string;
      },
      {
        success: boolean;
        statementId: string;
        totalAmount: number;
        perUnitCount: number;
        includedExpenseCount: number;
      }
    >(this.functions, 'generateChargeStatement');
    const res = await fn({
      syndicWorkspaceId: this.syndicWorkspaceId,
      buildingId: input.buildingId,
      periodStart: input.periodStart.toISOString(),
      periodEnd: input.periodEnd.toISOString(),
      currency: input.currency,
      expenseIds: input.expenseIds,
      notes: input.notes,
    });
    return res.data;
  }

  /**
   * Apply manual adjustments (exclusions + the three adjustment kinds) to a
   * DRAFT statement. The backend recomputes the per-unit breakdown and persists
   * the adjustments. Never touches the accounting ledger.
   */
  async updateChargeStatementAdjustments(input: {
    buildingId: string;
    statementId: string;
    excludedExpenseIds: string[];
    adjustments: ChargeAdjustmentDraft[];
  }) {
    const fn = httpsCallable<
      {
        syndicWorkspaceId: string;
        buildingId: string;
        statementId: string;
        excludedExpenseIds: string[];
        adjustments: ChargeAdjustmentDraft[];
      },
      {
        success: boolean;
        statementId: string;
        totalAmount: number;
        perUnitCount: number;
        includedExpenseCount: number;
        adjustmentCount: number;
      }
    >(this.functions, 'updateChargeStatementAdjustments');
    const res = await fn({
      syndicWorkspaceId: this.syndicWorkspaceId,
      buildingId: input.buildingId,
      statementId: input.statementId,
      excludedExpenseIds: input.excludedExpenseIds,
      adjustments: input.adjustments,
    });
    return res.data;
  }

  /**
   * Mark a charge statement as viewed in-app by a connected co-owner (sets the
   * in-app read timestamp on their unit receipt). Best-effort.
   */
  async acknowledgeChargeStatementView(input: {
    syndicWorkspaceId: string;
    buildingId: string;
    statementId: string;
    ownerWorkspaceId: string;
  }) {
    const fn = httpsCallable<typeof input, { success: boolean; stamped: number }>(
      this.functions,
      'acknowledgeChargeStatementView',
    );
    const res = await fn(input);
    return res.data;
  }

  /** Export a charge statement as an .xlsx workbook (Synthèse + Détail). */
  async exportChargeStatementXlsx(input: {
    buildingId: string;
    statementId: string;
    language?: string;
  }) {
    const fn = httpsCallable<
      { syndicWorkspaceId: string; buildingId: string; statementId: string; language?: string },
      { success: boolean; base64: string; filename: string }
    >(this.functions, 'exportChargeStatementXlsx');
    const res = await fn({
      syndicWorkspaceId: this.syndicWorkspaceId,
      buildingId: input.buildingId,
      statementId: input.statementId,
      language: input.language,
    });
    return res.data;
  }

  /**
   * Get the A4 printable HTML of a charge statement (building summary or one
   * unit's slice). The caller opens it in a new tab and prints-to-PDF.
   */
  async getChargeStatementDocumentHtml(input: {
    buildingId: string;
    statementId: string;
    scope?: 'building' | 'unit';
    unitId?: string;
    language?: string;
  }) {
    const fn = httpsCallable<
      {
        syndicWorkspaceId: string;
        buildingId: string;
        statementId: string;
        scope?: 'building' | 'unit';
        unitId?: string;
        language?: string;
      },
      { success: boolean; html: string; title: string; filename: string }
    >(this.functions, 'getChargeStatementDocumentHtml');
    const res = await fn({
      syndicWorkspaceId: this.syndicWorkspaceId,
      buildingId: input.buildingId,
      statementId: input.statementId,
      scope: input.scope,
      unitId: input.unitId,
      language: input.language,
    });
    return res.data;
  }

  /**
   * Send a communication (convocation / charge statement / announcement) to the
   * building's co-owners over the selected channels. The backend resolves the
   * recipients, renders the document, fans out, and records the history doc.
   * `channels` never includes `postal` (the UI disables it — "Bientôt").
   */
  async sendBuildingCommunication(input: {
    buildingId: string;
    type: BuildingCommunicationType;
    channels: CommunicationChannel[];
    subject: string;
    body: string;
    language?: 'fr' | 'nl' | 'en';
    scopeUnitIds?: string[];
    chargeStatementId?: string;
    meetingDate?: string;
    meetingLocation?: string;
    agendaItems?: { title: string; description?: string }[];
    /** When tied to a real assembly: enables server-side agenda + delivery receipts. */
    agId?: string;
  }) {
    const fn = httpsCallable<
      typeof input & { syndicWorkspaceId: string },
      {
        success: boolean;
        communicationId: string;
        status: 'sent' | 'partial' | 'failed';
        recipientCount: number;
        emailSent: number;
        inAppSent: number;
        skipped: { unitId: string; reason: string }[];
      }
    >(this.functions, 'sendBuildingCommunication');
    const res = await fn({ ...input, syndicWorkspaceId: this.syndicWorkspaceId });
    return res.data;
  }
}
